import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import {
  CalendarPlus,
  CheckSquare,
  ChevronDown,
  Flame,
  Mic,
  PenLine,
  Phone,
  Plus,
  Target,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCoachPersona } from '@/components/ask-coach'
import { BrainDumpTip } from '@/components/brain-dump-tip'
import { ComposerDialog } from '@/components/composer-dialog'
import { useQuickAdd } from '@/components/new-menu'
import { useCoachCall } from '@/lib/coach-call-store'
import { useIsCoaching } from '@/lib/coaching-mode'
import { dismissBrainDumpTip } from '@/lib/first-run'
import { openCapture } from '@/store/capture.store'

const FAB_KEY = 'fabDefault'
type FabScreen = 'today' | 'goals' | 'inbox' | 'events'
type ActionKey =
  | 'newGoal'
  | 'newTask'
  | 'newEvent'
  | 'newHabit'
  | 'brainDump'
  | 'callCoach'
  | 'newPost'
const ORDER: ActionKey[] = [
  'brainDump',
  'newGoal',
  'newTask',
  'newEvent',
  'newHabit',
  'callCoach',
  'newPost',
]
// The AI coach and Brain dump belong to the personal side.
const PERSONAL_ONLY: ActionKey[] = ['brainDump', 'callCoach']

function storedDefault(fallback: ActionKey): ActionKey {
  const v = typeof window !== 'undefined' ? localStorage.getItem(FAB_KEY) : null
  return v && (ORDER as string[]).includes(v) ? (v as ActionKey) : fallback
}

// ── Split FAB ─────────────────────────────────────────────────────────────────
// Left half runs the default action (remembered); the chevron opens a menu
// (Brain dump · New Goal · New Task · New Event · New Habit · Call coach · New Post). Picking a menu item
// runs it and makes it the new default.

export function GoalTreeFab({
  expanded,
  screen,
}: {
  expanded: boolean
  // Goals pins the default to create goal, Everyday's Events tab to new event; elsewhere it's the last action taken.
  screen?: FabScreen
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const quickAdd = useQuickAdd()
  const coach = useCoachPersona()
  const coaching = useIsCoaching()
  // The minimised-call pill sits where this button does. The call controls win.
  const callOverNav = useCoachCall((s) => s.navCall != null)
  const [open, setOpen] = useState(false)
  const keyboardInset = useKeyboardInset()
  const [primary, setPrimary] = useState<ActionKey>(() =>
    storedDefault(screen === 'goals' ? 'newGoal' : 'newTask'),
  )
  const [postOpen, setPostOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  const [expandedWidth, setExpandedWidth] = useState(168)

  // Measure the (widest possible) label so the pill width is stable across actions.
  useEffect(() => {
    if (labelRef.current) setExpandedWidth(18 + 8 + labelRef.current.offsetWidth + 36 + 32)
  }, [primary])

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: PointerEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('pointerdown', onPointerDown)
    return () => window.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (!expanded) setOpen(false)
  }, [expanded])

  const ACTIONS: Record<
    ActionKey,
    { icon: React.ReactNode; label: string; sub: string; run: () => void }
  > = {
    newGoal: {
      icon: <Target className="h-4 w-4" />,
      label: t('fab.newGoal'),
      sub: t('nav.newMenuGoalHint'),
      run: () => {
        sessionStorage.setItem('newgoal_from', window.location.pathname)
        void navigate({ to: '/goals/new', search: { goal: undefined } })
      },
    },
    newTask: {
      icon: <CheckSquare className="h-4 w-4" />,
      label: t('fab.newTask'),
      sub: t('nav.newMenuTaskHint'),
      run: () => void quickAdd('tasks'),
    },
    newEvent: {
      icon: <CalendarPlus className="h-4 w-4" />,
      label: t('fab.newEvent'),
      sub: t('nav.newMenuEventHint'),
      run: () => void quickAdd('event'),
    },
    newHabit: {
      icon: <Flame className="h-4 w-4" />,
      label: t('fab.newHabit'),
      sub: t('nav.newMenuHabitHint'),
      run: () => void quickAdd('habits'),
    },
    brainDump: {
      icon: <Mic className="h-4 w-4" />,
      label: t('voice.title'),
      sub: t('voice.menuSubtitle'),
      run: openCapture,
    },
    callCoach: {
      icon: <Phone className="h-4 w-4" />,
      label: t('fab.callCoach', { coach: coach.name }),
      sub: t('fab.callCoachHint'),
      run: () =>
        window.dispatchEvent(new CustomEvent('open-coach', { detail: { startCall: true } })),
    },
    newPost: {
      icon: <PenLine className="h-4 w-4" />,
      label: t('fab.newPost'),
      sub: t('nav.newMenuPostHint'),
      run: () => setPostOpen(true),
    },
  }

  function pick(key: ActionKey) {
    setOpen(false)
    setPrimary(key)
    localStorage.setItem(FAB_KEY, key)
    ACTIONS[key].run()
  }

  const primaryKey: ActionKey =
    screen === 'goals'
      ? 'newGoal'
      : screen === 'events'
        ? 'newEvent'
        : coaching && PERSONAL_ONLY.includes(primary)
          ? 'newTask'
          : primary
  const Primary = ACTIONS[primaryKey]

  return (
    <>
      {/* Hidden widest-label for width measurement */}
      <span
        ref={labelRef}
        aria-hidden
        className="pointer-events-none invisible fixed left-0 top-0 whitespace-nowrap text-sm font-bold"
      >
        {Primary.label}
      </span>

      <div
        ref={containerRef}
        className={cn(
          'pointer-events-none fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] right-4 z-50 flex flex-col items-end gap-2 lg:hidden',
          callOverNav && 'hidden',
        )}
        // The page resizes for the on-screen keyboard (interactive-widget=resizes-content),
        // which would lift the button onto it; push it back down so the keyboard covers it.
        style={keyboardInset ? { transform: `translateY(${keyboardInset}px)` } : undefined}
      >
        {/* Menu: same card as the bottom-nav coach menu */}
        <div
          role="menu"
          className={cn(
            'flex w-max min-w-40 max-w-[calc(100vw-2rem)] flex-col rounded-xl bg-popover p-1 text-popover-foreground shadow-2xl ring-1 ring-foreground/10 transition-all duration-200',
            open
              ? 'pointer-events-auto translate-y-0 opacity-100'
              : 'pointer-events-none translate-y-1 opacity-0',
          )}
        >
          {ORDER.filter((key) => !coaching || !PERSONAL_ONLY.includes(key)).map((key) => {
            const a = ACTIONS[key]
            return (
              <button
                key={key}
                type="button"
                role="menuitem"
                onClick={() => pick(key)}
                className={cn(
                  'flex cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none',
                  key === primaryKey && 'bg-muted/60',
                )}
              >
                <span className="mt-0.5 text-primary-text [&_svg]:size-4">{a.icon}</span>
                <span className="flex min-w-0 flex-col">
                  {a.label}
                  <span className="text-xs font-normal text-muted-foreground">{a.sub}</span>
                </span>
              </button>
            )
          })}
        </div>

        {/* Brain dump: a small mic above the pill, tucked away while scrolling down. */}
        {!coaching && !open && (
          <div
            className={cn(
              'relative transition-all duration-200',
              expanded
                ? 'pointer-events-auto translate-y-0 opacity-100'
                : 'pointer-events-none translate-y-2 opacity-0',
            )}
          >
            {expanded && <BrainDumpTip placement="fab" />}
            <button
              type="button"
              onClick={openCapture}
              aria-label={t('voice.title')}
              tabIndex={expanded ? 0 : -1}
              className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-popover text-primary-text shadow-lg ring-1 ring-foreground/10 transition-colors hover:bg-accent"
            >
              <Mic className="size-[18px]" strokeWidth={2.25} />
            </button>
          </div>
        )}

        {/* Split pill: default action (left) · chevron (right) */}
        <div className="pointer-events-auto relative">
          <div className="flex h-11 overflow-hidden rounded-full bg-primary text-white shadow-lg dark:text-[#0a0a0a]">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                Primary.run()
              }}
              style={{ width: expanded ? expandedWidth - 36 : 40 }}
              className={cn(
                'flex shrink-0 cursor-pointer items-center overflow-hidden transition-[width,padding] duration-200',
                expanded ? 'pl-4 pr-2' : 'justify-center',
              )}
            >
              <Plus className="h-[18px] w-[18px] shrink-0" strokeWidth={2.5} />
              <span
                className={cn(
                  'overflow-hidden whitespace-nowrap text-sm font-bold transition-all duration-200',
                  expanded ? 'ml-2 w-auto opacity-100' : 'ml-0 w-0 opacity-0',
                )}
              >
                {Primary.label}
              </span>
            </button>

            <div className="flex w-9 shrink-0 items-center">
              <div className="h-5 w-px bg-white/20 dark:bg-[#0a0a0a]/20" />
              <button
                type="button"
                onClick={() => {
                  dismissBrainDumpTip()
                  setOpen((v) => !v)
                }}
                className="flex h-full w-full cursor-pointer items-center justify-center"
                aria-label="More options"
              >
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 transition-transform duration-200',
                    open && 'rotate-180',
                  )}
                  strokeWidth={2.5}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <ComposerDialog open={postOpen} onOpenChange={setPostOpen} />
    </>
  )
}

const NON_TEXT_INPUTS = new Set([
  'button',
  'checkbox',
  'radio',
  'range',
  'submit',
  'reset',
  'file',
  'color',
])

function isTextField(el: EventTarget | null) {
  if (!(el instanceof HTMLElement)) return false
  if (el.isContentEditable || el.tagName === 'TEXTAREA') return true
  return el instanceof HTMLInputElement && !NON_TEXT_INPUTS.has(el.type)
}

// How far the page has shrunk for the on-screen keyboard: the height while no text
// field is focused, minus the current height. Zero where the keyboard overlays instead
// of resizing (iOS Safari).
function useKeyboardInset() {
  const [inset, setInset] = useState(0)
  useEffect(() => {
    let base = window.innerHeight
    const update = () => {
      if (!isTextField(document.activeElement)) {
        base = window.innerHeight
        setInset(0)
      } else setInset(Math.max(0, base - window.innerHeight))
    }
    window.addEventListener('resize', update)
    document.addEventListener('focusin', update)
    document.addEventListener('focusout', update)
    return () => {
      window.removeEventListener('resize', update)
      document.removeEventListener('focusin', update)
      document.removeEventListener('focusout', update)
    }
  }, [])
  return inset
}

// The FAB collapsing to its icon while the screen's scroller moves down.
export function ScrollFab({ screen }: { screen?: FabScreen }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [expanded, setExpanded] = useState(true)
  useEffect(() => {
    setExpanded(true)
    const el = document.querySelector('[data-scroll-reset]')
    if (!el) return
    let last = el.scrollTop
    function onScroll() {
      const dy = el!.scrollTop - last
      last = el!.scrollTop
      if (dy > 4) setExpanded(false)
      else if (dy < -4) setExpanded(true)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [pathname])
  return <GoalTreeFab expanded={expanded} screen={screen} />
}
